import { VERIFICATION_TIER, VERIFY_PERMISSION } from '../../constants';
import useReviewForm from '../useReviewForm';
import { useCallback, useEffect, useRef, useState } from 'react';

import useAccessToken from 'hooks/useAccessToken';
import type { DecodedToken } from 'hooks/useAccessToken/types';
import useFormulationReview from 'hooks/useFormulationReview';

import type { ActionState, ReviewState, UseDataParams, UseDataReturn } from './types';

const useData = ({
    chemicalId,
    isOpen,
    onClose,
    onFormulationChanged
}: UseDataParams): UseDataReturn => {
    const { decodedToken } = useAccessToken();
    const { clearFormulationVerification, fetchFormulationReview, saveFormulationVerification } =
        useFormulationReview();
    const [reviewState, setReviewState] = useState<ReviewState>({ kind: 'loading' });
    const [actionState, setActionState] = useState<ActionState>('idle');
    const [actionError, setActionError] = useState<string | null>(null);
    // A review can take seconds; ignore replies from a request the user has moved past.
    const requestIdRef = useRef(0);

    const permissions = (decodedToken as DecodedToken | undefined)?.permissions ?? [];
    const canVerify = permissions.includes(VERIFY_PERMISSION);
    const isBusy = actionState !== 'idle';
    const form = useReviewForm({ canVerify, isBusy, reviewState });

    const loadReview = useCallback(async () => {
        requestIdRef.current += 1;

        const requestId = requestIdRef.current;

        setReviewState({ kind: 'loading' });
        setActionError(null);

        const { data, errorMessage } = await fetchFormulationReview({ id: chemicalId });

        if (requestId !== requestIdRef.current) {
            return;
        }

        setReviewState(
            data ? { kind: 'loaded', review: data } : { kind: 'error', message: errorMessage ?? '' }
        );
    }, [chemicalId, fetchFormulationReview]);

    useEffect(() => {
        if (isOpen) {
            loadReview();
        }
        // Load once per open; the review endpoint is rate-limited and slow.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, chemicalId]);

    const finishAction = (errorMessage: string | null) => {
        setActionState('idle');
        setActionError(errorMessage);

        if (!errorMessage) {
            onFormulationChanged();
            onClose();
        }
    };

    const onConfirm = async () => {
        const { selectedStatement } = form;

        if (!selectedStatement?.suggestedCode || !form.isConfirmEnabled) {
            return;
        }

        setActionState('saving');

        const trimmedNote = form.labelMixingNote.trim();
        const { errorMessage } = await saveFormulationVerification({
            id: chemicalId,
            request: {
                evidence: form.evidence.trim(),
                evidenceSource: selectedStatement.documentUrl,
                formulationCode: selectedStatement.suggestedCode.toUpperCase(),
                tier: VERIFICATION_TIER,
                ...(trimmedNote && { labelMixingNote: trimmedNote })
            }
        });

        finishAction(errorMessage);
    };

    const onClear = async () => {
        if (!form.canClear || isBusy) {
            return;
        }

        setActionState('clearing');

        const { errorMessage } = await clearFormulationVerification({ id: chemicalId });

        finishAction(errorMessage);
    };

    // Closing mid-save would let the reply land on a reopened modal; closing mid-load
    // drops the stale reply via requestIdRef.
    const onRequestClose = () => {
        if (isBusy) {
            return;
        }

        requestIdRef.current += 1;
        setReviewState({ kind: 'loading' });
        setActionError(null);
        onClose();
    };

    return {
        actionError,
        canClear: form.canClear,
        canVerify,
        documents: form.documents,
        evidence: form.evidence,
        hasContradiction: form.hasContradiction,
        isBusy,
        isConfirmEnabled: form.isConfirmEnabled,
        labelMixingNote: form.labelMixingNote,
        onClear,
        onConfirm,
        onEvidenceChange: form.onEvidenceChange,
        onLabelMixingNoteChange: form.onLabelMixingNoteChange,
        onRequestClose,
        onRetry: loadReview,
        onSelectStatement: form.onSelectStatement,
        reviewState,
        selectedStatementKey: form.selectedStatementKey,
        suggestedCodes: form.suggestedCodes
    };
};

export default useData;
